import { dropTask } from 'ember-concurrency';
import PlanningOrder from 'volta/models/planning-order';
import { defaultErrorHandler } from 'volta/utils/error-utils';

import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type BvFlashService from 'volta/services/bv-flash';
import type IntlService from 'ember-intl/services/intl';
import type { IManyPlanningOrdersUpdatedResult } from 'volta/models/types/planning-order';
import type LabelService from 'volta/services/label-service';

interface IArgs {
  orderNum?: string;
  externalOrderNum?: string;
  renderInPlace: boolean;
  onOrderNumUpdate?: (result: IManyPlanningOrdersUpdatedResult) => void;
  onExternalOrderNumUpdate?: (result: IManyPlanningOrdersUpdatedResult) => void;
}

export default class PoNumEditor extends Component<IArgs> {
  @service bvFlash!: BvFlashService;
  @service intl!: IntlService;
  @service labelService!: LabelService;

  @tracked newOrderNum?: string;
  @tracked newExternalOrderNum?: string;
  @tracked backendErrors?: Record<string, string>;

  @action
  async handleOrderNumUpdate() {
    const { orderNum } = this.args;
    if (!this.newOrderNum || this.newOrderNum.length === 0 || !orderNum) {
      return;
    }

    try {
      const updatedPOs = await this.updateOrderNumTask.perform(orderNum, this.newOrderNum);

      this.bvFlash.success(
        this.intl.t('poWorkbench.orderNumUpdateSuccess', { old: orderNum, new: this.newOrderNum })
      );

      this.newOrderNum = undefined;
      this.backendErrors = undefined;
      this.args.onOrderNumUpdate?.(updatedPOs);
      return updatedPOs;
    } catch (backendErrors) {
      this.backendErrors = backendErrors;
      return;
    }
  }

  @action
  async handleExternalDataUpdate() {
    const { orderNum } = this.args;
    let externalNum = this.newExternalOrderNum;

    if (!this.newExternalOrderNum || this.newExternalOrderNum.length === 0) {
      externalNum = undefined;
    }

    try {
      const updatedPOs = await this.updateExternalDataTask.perform(orderNum!, externalNum);

      this.bvFlash.success(
        this.intl.t('poWorkbench.externalOrderNumUpdateSuccess', { num: this.newExternalOrderNum })
      );
      this.newExternalOrderNum = undefined;
      this.backendErrors = undefined;
      this.args.onExternalOrderNumUpdate?.(updatedPOs);
      return updatedPOs;
    } catch (backendErrors) {
      this.backendErrors = backendErrors;
      return;
    }
  }

  @action
  closeOrderNumEditor() {
    this.newExternalOrderNum = undefined;
    this.backendErrors = undefined;
  }

  @action
  closeExternalOrderNumEditor() {
    this.newOrderNum = undefined;
    this.backendErrors = undefined;
  }

  /**
   * Tasks for calling the `UpdateOrderNum` command
   *
   * @param {string} oldOrderNum Old order num
   * @param {string} newOrderNum New order num
   */
  updateOrderNumTask = dropTask(async (oldOrderNum: string, newOrderNum: string) => {
    try {
      const response: {
        data: IManyPlanningOrdersUpdatedResult;
      } = await PlanningOrder.updateOrderNum({
        oldOrderNum,
        newOrderNum
      });
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      throw this.labelService.formValidationsFromApiError(error);
    }
  });

  /**
   * Tasks for calling the `UpdateExternalData` command
   *
   * @param {string} orderNum Order num
   * @param {string} newExternalOrderNum New external order num
   */
  updateExternalDataTask = dropTask(async (orderNum: string, newExternalOrderNum?: string) => {
    try {
      const response = await PlanningOrder.updateExternalData({
        orderNum,
        newExternalOrderNum
      });
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      throw this.labelService.formValidationsFromApiError(error);
    }
  });
}
